import { z } from 'zod'

/**
 * The legacy-subject recovery queue.
 *
 * A subject is a legacy catalogue row that matches several pieces of one article
 * and that no rule can decide. It is not a piece and never becomes one: nothing
 * in this contract carries a create verb, and the decision below moves relations
 * onto a piece that already exists or records that none of them is the answer.
 *
 * Typed English identifiers on the wire, Russian only in the page. A vocabulary
 * value is never translated into the wire and a Russian sentence is never
 * parsed back out of it.
 */

/** What the operator decided. `matched` is the only outcome naming a piece. */
export const RecoveryOutcomeSchema = z.enum([
  'matched',
  'not_any_of_these',
  'cannot_determine',
])

/**
 * What the decision stands on, and the reason this surface exists.
 *
 * A person may look at a photograph and recognise a piece. That is a real
 * decision and it is stored as what it is. The two members are never collapsed,
 * never defaulted into one another and never summarised as a single "decided":
 * a later reader who cannot tell them apart inherits a certainty nobody had.
 */
export const RecoveryBasisSchema = z.enum([
  'exact_source_identity',
  'operator_recognition',
])

/**
 * Where the subject stands, as `forge.product_recovery.contracts.Status`.
 *
 * `open` means a person can act on it, which is why `held` is a separate member
 * rather than a flavour of open: a held row is one the server refuses a decision
 * for until the owner releases it. `cannot_determine` is a settled outcome that
 * leaves the subject in the queue, because pieces identical on every recorded
 * fact exist and a queue that only accepts a match stalls on them.
 *
 * The screen never sees `matched`: the server collapses the three settled
 * dispositions into `closed` on the way out, so what a decision actually was is
 * readable only from the decision history.
 */
export const RecoverySubjectStatusSchema = z.enum([
  'open',
  'held',
  'cannot_determine',
  'closed',
])

/** `all` is for tools. It is never the operator default. */
export const RecoveryStatusFilterSchema = z.union([
  RecoverySubjectStatusSchema,
  z.literal('all'),
])

/**
 * The stored vocabularies, deliberately wider than the request's and kept apart
 * from it.
 *
 * The extra members are decisions the system made rather than a person: an
 * automatic article-singleton match, a reviewed finding with no target, an
 * unattached row deleted for carrying nothing. An audit that cannot show a
 * machine's decision beside a person's is not an audit.
 *
 * Note that one outcome is spelled two ways on purpose. A request says
 * `not_any_of_these` and the store answers `none_of_these`. Mapping the two onto
 * one member would look like tidying and would be a silent unification, so the
 * two enums never meet.
 */
export const RecoveryStoredOutcomeSchema = z.enum([
  'matched',
  'none_of_these',
  'cannot_determine',
  'deleted',
  'distributed',
])

export const RecoveryStoredBasisSchema = z.enum([
  'exact_source_identity',
  'exact_article_singleton',
  'operator_recognition',
  'evidence_review',
  'unattached_no_candidate',
  'article_group_choice',
])

/** The three states `forge.product.current_state` admits. */
export const PieceStateSchema = z.enum(['in_stock', 'in_transit', 'sold'])

export const RecoveryAttachmentCountsSchema = z.object({
  salesLinks: z.number().int().nonnegative(),
  orderLines: z.number().int().nonnegative(),
  listings: z.number().int().nonnegative(),
  media: z.number().int().nonnegative(),
}).strict()

/**
 * One identifier or source fact a candidate carries. `kind` is read leniently:
 * an identifier family this build has not seen still reaches the operator under
 * its own name, where a strict enum would replace a real fact with a parse
 * failure. The page renders an unknown kind as its own token rather than
 * inventing a sentence for it.
 */
export const RecoverySourceFactSchema = z.object({
  kind: z.string().min(1),
  value: z.string().min(1),
  sourceRef: z.string().nullable(),
}).strict()

/**
 * One candidate piece.
 *
 * `currentState` and `weightGrams` are value-xor-reason pairs, mirroring the
 * `num_nonnulls(value, reason) = 1` constraints `forge.product` already carries
 * on both columns. The other facts have no stored reason, so their absence is
 * rendered as a dash and the page says nothing it cannot support.
 */
export const RecoveryCandidateSchema = z.object({
  productId: z.string().uuid(),
  title: z.string().nullable(),
  article: z.string().nullable(),
  size: z.string().nullable(),
  weightGrams: z.number().nullable(),
  weightUnavailableReason: z.string().nullable(),
  uin: z.string().nullable(),
  /** What the source reported, kept beside `uin` rather than folded into it. */
  reportedUin: z.string().nullable(),
  seriesRef: z.string().nullable(),
  currentState: PieceStateSchema.nullable(),
  currentStateUnavailableReason: z.string().nullable(),
  sourceIdentities: z.array(RecoverySourceFactSchema),
  evidence: z.array(RecoverySourceFactSchema),
}).strict().refine(
  (candidate) => (candidate.weightGrams === null) === (candidate.weightUnavailableReason !== null),
  { message: 'weightGrams and weightUnavailableReason are exclusive' },
).refine(
  (candidate) => (candidate.currentState === null) === (candidate.currentStateUnavailableReason !== null),
  { message: 'currentState and currentStateUnavailableReason are exclusive' },
)

/** The legacy row itself. It carries no weight: the legacy population has none. */
export const RecoveryOriginalProductSchema = z.object({
  productId: z.string().uuid(),
  title: z.string().nullable(),
  article: z.string().nullable(),
  size: z.string().nullable(),
}).strict()

export const RecoveryAttachedOrderSchema = z.object({
  orderId: z.string().min(1),
  externalOrderId: z.string().nullable(),
  providerCode: z.string().min(1),
  placedAt: z.iso.datetime({ offset: true }).nullable(),
}).strict()

export const RecoveryAttachedSaleSchema = z.object({
  salesLinkId: z.string().min(1),
  orderId: z.string().nullable(),
  providerCode: z.string().min(1),
  matchRule: z.string().nullable(),
}).strict()

export const RecoveryAttachedListingSchema = z.object({
  listingId: z.string().min(1),
  providerCode: z.string().min(1),
  accountCode: z.string().min(1),
  externalOfferId: z.string().nullable(),
}).strict()

export const RecoveryAttachedMediaSchema = z.object({
  mediaId: z.string().min(1),
  kind: z.string().min(1),
  isPrimary: z.boolean(),
}).strict()

/** What moves if the decision is made. A person deciding blind is the failure. */
export const RecoveryAttachedSchema = z.object({
  orders: z.array(RecoveryAttachedOrderSchema),
  salesLinks: z.array(RecoveryAttachedSaleSchema),
  listings: z.array(RecoveryAttachedListingSchema),
  media: z.array(RecoveryAttachedMediaSchema),
}).strict()

export const RecoverySubjectSummarySchema = z.object({
  id: z.string().uuid(),
  /** Bumped by every write. A decision names the revision it was taken against. */
  revision: z.number().int().positive(),
  status: RecoverySubjectStatusSchema,
  reason: z.string().nullable(),
  legacyProductId: z.string().uuid(),
  article: z.string().nullable(),
  title: z.string().nullable(),
  candidateCount: z.number().int().nonnegative(),
  attachmentCounts: RecoveryAttachmentCountsSchema,
}).strict()

/**
 * One page of the queue.
 *
 * `total` is the filtered population and moves with the filter. `openCount` and
 * `heldCount` describe the whole queue and do not, which is what lets the screen
 * and the Warehouse chip state one number for one population. `appliedStatus`
 * says which filter produced `total`, so a count can never be read as answering
 * for a population it was not measured over.
 */
export const RecoverySubjectPageSchema = z.object({
  appliedStatus: RecoveryStatusFilterSchema,
  items: z.array(RecoverySubjectSummarySchema),
  total: z.number().int().nonnegative(),
  openCount: z.number().int().nonnegative(),
  heldCount: z.number().int().nonnegative(),
  limit: z.number().int().positive(),
  offset: z.number().int().nonnegative(),
}).strict()

export const RecoverySubjectDetailSchema = z.object({
  id: z.string().uuid(),
  revision: z.number().int().positive(),
  status: RecoverySubjectStatusSchema,
  reason: z.string().nullable(),
  legacyProductId: z.string().uuid(),
  article: z.string().nullable(),
  title: z.string().nullable(),
  candidateCount: z.number().int().nonnegative(),
  attachmentCounts: RecoveryAttachmentCountsSchema,
  originalProduct: RecoveryOriginalProductSchema,
  attached: RecoveryAttachedSchema,
  candidates: z.array(RecoveryCandidateSchema),
}).strict()

/**
 * The persisted decision, read back exactly as it was stored.
 *
 * `basis` is non-null only on `matched`, and it remains the member the operator
 * named. Nothing here derives it, defaults it or drops it on reload.
 */
export const RecoveryDecisionSchema = z.object({
  decisionId: z.string().uuid(),
  subjectId: z.string().uuid(),
  outcome: RecoveryOutcomeSchema,
  targetProductId: z.string().uuid().nullable(),
  basis: RecoveryBasisSchema.nullable(),
  evidenceNote: z.string().nullable(),
  /** Server-owned. The client never sends an actor or a time. */
  decidedBy: z.string().min(1),
  decidedAt: z.iso.datetime({ offset: true }),
}).strict().refine(
  (decision) => (decision.outcome === 'matched') === (decision.targetProductId !== null),
  { message: 'only a matched decision names a target piece' },
).refine(
  (decision) => (decision.outcome === 'matched') === (decision.basis !== null),
  { message: 'only a matched decision carries a basis' },
)

export const RecoveryDecisionResultSchema = z.object({
  decision: RecoveryDecisionSchema,
  subject: RecoverySubjectDetailSchema,
}).strict()

/**
 * What a decision actually moved, as the server stores it: an open JSON object
 * whose keys depend on the outcome. A match carries the relation rows before and
 * after, a deletion carries the deleted row, a targetless finding carries the
 * candidates it was taken over.
 *
 * It is modelled as the open map the server declares rather than as an object of
 * optional fields, because an optional field would claim a shape the contract
 * does not promise. Reading it is `summariseMovement`'s job, and nothing here
 * puts the raw object in front of an operator.
 */
export const RecoveryMovementEvidenceSchema = z.record(z.string(), z.unknown())

/**
 * One stored decision, person's or machine's, read back from the audit.
 *
 * `basis` is never null here, unlike the request's: a targetless finding is
 * stored as `evidence_review` and a deletion as `unattached_no_candidate`. This
 * type is not the request type with wider enums, and it is never narrowed to it.
 */
export const RecoveryAuditDecisionSchema = z.object({
  decisionId: z.string().uuid(),
  subjectId: z.string().uuid(),
  /** The revision the decision was taken against, which the request carries too. */
  expectedRevision: z.number().int().positive(),
  outcome: RecoveryStoredOutcomeSchema,
  targetProductId: z.string().uuid().nullable(),
  /** The live piece the stored target reaches through merge pointers; never replaces the stored id. */
  targetResolvedProductId: z.string().uuid().nullable(),
  basis: RecoveryStoredBasisSchema,
  evidenceNote: z.string(),
  decidedBy: z.string().min(1),
  decidedAt: z.iso.datetime({ offset: true }),
  movementEvidence: RecoveryMovementEvidenceSchema,
}).strict()

export const RecoveryDecisionHistorySchema = z.object({
  items: z.array(RecoveryAuditDecisionSchema),
}).strict()

export type RecoveryOutcome = z.infer<typeof RecoveryOutcomeSchema>
export type RecoveryBasis = z.infer<typeof RecoveryBasisSchema>
export type RecoveryStoredOutcome = z.infer<typeof RecoveryStoredOutcomeSchema>
export type RecoveryStoredBasis = z.infer<typeof RecoveryStoredBasisSchema>
export type RecoverySubjectStatus = z.infer<typeof RecoverySubjectStatusSchema>
export type RecoveryStatusFilter = z.infer<typeof RecoveryStatusFilterSchema>
export type RecoveryMovementEvidence = z.infer<typeof RecoveryMovementEvidenceSchema>
export type RecoveryAuditDecision = z.infer<typeof RecoveryAuditDecisionSchema>
export type RecoveryDecisionHistory = z.infer<typeof RecoveryDecisionHistorySchema>
export type PieceState = z.infer<typeof PieceStateSchema>
export type RecoverySourceFact = z.infer<typeof RecoverySourceFactSchema>
export type RecoveryCandidate = z.infer<typeof RecoveryCandidateSchema>
export type RecoveryAttachmentCounts = z.infer<typeof RecoveryAttachmentCountsSchema>
export type RecoveryAttached = z.infer<typeof RecoveryAttachedSchema>
export type RecoverySubjectSummary = z.infer<typeof RecoverySubjectSummarySchema>
export type RecoverySubjectPage = z.infer<typeof RecoverySubjectPageSchema>
export type RecoverySubjectDetail = z.infer<typeof RecoverySubjectDetailSchema>
export type RecoveryDecision = z.infer<typeof RecoveryDecisionSchema>
export type RecoveryDecisionResult = z.infer<typeof RecoveryDecisionResultSchema>
